import { LOGGING, LANGUAGE_SETTINGS, URL_SETTINGS, HEADER_EXPLORE_DEFAULT, HEADER_DISCOVER_DEFAULT, HEADER_VARIANT, AREA_SELECTOR, MAP_DATA_STRATEGY, EXPLORE_TAG_SETTINGS, BLOG_ENABLED } from '/settings.js';
import { parseUrlForIds, parseUrlForBlogPost, parseUrlParams, updateUrl, updateUrlForExplore } from './utils/url-utils.js';
import { getSpotsByIds, getSpotsBySearch, getSpotsByExplore, getSpotsByDiscover, getMapBySlug } from './utils/spot-queries.js';
import { DATA_PATHS } from './dataPaths.js';
import { initializeLoading } from './components/loading.js';
import { info as showAlert } from './components/alerts.js';
import { initializeLanguage, setLanguageSilent } from './translations/translations.js';
import { initializeHeader, initializeMenu } from './header/index.js';
import { initializeExploreSearch } from './header/header-explore.js';
import { initializeDiscoverBar } from './header/header-discover.js';
import { updateMapView } from './map/map-transitions.js';
import { initializePerformanceDebug } from './components/performance-debug.js';
// Blog is lazy-loaded only when BLOG_ENABLED is true
import { initializeCards, setCardsContext, hideCardsBanner } from './cards/index.js';
import { initializeAttribution } from './components/attribution.js';
import { initializeAreaSelector, getAreaBySlug, getLastSelectedAreaSlug } from './area-selector/index.js';
import { clearAllSpots } from './map/map-visibility.js'; // Import clearAllSpots
import { clearAllUI } from './utils/clearAll.js'; // Import clearAllUI
import { getText } from './translations/translations.js';

// Helper function for blog logging
function logBlog(message, data = null) {
    if (LOGGING.ENABLED && LOGGING.BLOG) {
        if (data) {
            console.log(`[BLOG] ${message}`, data);
        } else {
            console.log(`[BLOG] ${message}`);
        }
    }
}

function resolveStartupRoute({ blogData, directIds, areaSlug, mode, params }) {
    // 1) Blog (highest priority)
    if (BLOG_ENABLED && blogData && blogData.language && blogData.postSlug) {
        return { type: 'blog', blogData };
    }

    // 2) Direct IDs (legacy ?id=..., bypass favorites modes)
    if ((!mode || (mode !== 'favorites' && mode !== 'myfavorites')) && directIds && directIds.length > 0 && URL_SETTINGS.IDs) {
        return { type: 'ids', ids: directIds, areaSlug };
    }

    // 3) No area -> need selector
    if (!areaSlug) {
        return { type: 'area-selector' };
    }

    // 4) Favorites
    if ((mode === 'favorites' || mode === 'myfavorites') && URL_SETTINGS.favorites) {
        const idsString = params?.[0] || '';
        return { type: 'favorites', idsString, areaSlug };
    }

    // 5) Explore
    if (mode === 'explore' && URL_SETTINGS.explore) {
        return { type: 'explore', areaSlug, params: params || [] };
    }

    // 6) Search
    if (mode === 'search' && URL_SETTINGS.search) {
        return { type: 'search', areaSlug, params: params || [] };
    }

    // 7) Discover / map
    if ((mode === 'map' || mode === 'discover' || (mode === null && params && params.length > 0)) && URL_SETTINGS.discover) {
        return { type: 'discover', areaSlug, params: params || [] };
    }

    // 8) Default: area only (no specific flow)
    return { type: 'area-only', areaSlug };
}

export async function initializeApp() {
    try {
        // PRIORITY 1: Check database version FIRST (before any caching)
        // This ensures cache is invalidated if database was updated
        try {
            const { checkDatabaseVersion } = await import('./utils/cache/data-cache.js');
            await checkDatabaseVersion(); // Non-blocking, but ensures version is checked early
        } catch (error) {
            // Don't block if version check fails, but log it
            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.warn('[App] Failed to check database version:', error);
            }
        }

        // PRIORITY 2: Preload loading logo (before showing loading screen)
        // This ensures the logo is ready immediately when loading appears
        try {
            const { preloadLoadingLogo } = await import('./utils/cache/static-assets-cache.js');
            await preloadLoadingLogo(); // Wait for logo to be preloaded
        } catch (error) {
            // Don't block if preload fails, but log it
            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.warn('[App] Failed to preload loading logo:', error);
            }
        }

        // Initialize enhanced loading system with task tracking
        const loading = initializeLoading();
        // Store globally for error handling
        window.__loadingManager = loading;

        // Register critical tasks
        const completeLanguage = loading.registerTask('language');
        const completeMap = loading.registerTask('map');
        const completeUI = loading.registerTask('ui-initialization');

        // --- Setup Event Listeners for Area Switching ---
        window.addEventListener('areaChanged', async (e) => {
            const area = e.detail.area;
            if (!area) return;
            if (LOGGING.ENABLED && LOGGING.GENERAL) {
                console.log('[App] Area changed to:', area.slug);
            }

            // Clear UI first (popups, sidebars, etc.)
            clearAllUI();

            // DON'T clear caches when changing area - they're already differentiated by area in their keys
            // This allows users to switch between areas and have instant access to previously loaded data
            // Caches are structured as:
            // - exploreCardsCache: areaId-packId-tagIds
            // - exploreTagsCache: areaId-packId
            // - exploreMinimalResultsCache: areaId-packId
            // - mapsMinimalResultsCache: areaId
            // - cardsCache/spotsCache: spotId (unique globally, safe to keep)
            // - sessionStorage: URLs include areaId parameter
            // 
            // Benefits:
            // - Instant access when returning to a previously visited area
            // - Less server load (no redundant API calls)
            // - Better user experience (faster navigation)
            // - Data doesn't change frequently, so caching is safe

            // Use handleAreaSelection for consistency - it handles preloading, cards context, header initialization, and finalization
            // Note: Map movement is already handled by area-selector, but handleAreaSelection's map.setView is safe (harmless if already at target)
            const mapModule = await import('./map/index.js');
            await handleAreaSelection(area, null, [], mapModule);
            // Note: handleAreaSelection calls finalizeInitialization which already initializes the header, so no need to call it again here
        });

        // --- URL Parsing ---
        const directIds = parseUrlForIds();
        let { areaSlug, mode, params } = parseUrlParams();

        // If the URL is ambiguous (e.g., /{area}/{candidate}/...), we may have a candidate
        // that is a pack slug — but parseUrlParams treats unknown second segments as 'map' by default.
        // Use a runtime disambiguation helper that checks the area's packs (cached) and
        // converts the mode to 'explore' if the second segment matches a pack for that area.
        if (areaSlug && mode === 'map' && params && params.length > 0) {
            try {
                const { disambiguateAreaSecondSegment } = await import('./utils/url-utils.js');
                const result = await disambiguateAreaSecondSegment(areaSlug, params);
                if (result.mode !== mode) {
                    mode = result.mode;
                    params = result.params;
                    if (LOGGING.ENABLED && LOGGING.DEBUG) console.log('[App] URL disambiguation applied:', mode, params);
                }
            } catch (e) {
                if (LOGGING.ENABLED && LOGGING.DEBUG) console.warn('[App] URL disambiguation failed:', e);
            }
        }

        // Parse blog data only if blog is enabled
        const blogData = BLOG_ENABLED ? parseUrlForBlogPost() : null;
        if (BLOG_ENABLED && blogData) logBlog('App initialization - blogData:', blogData);

        // Initialize language system (tracked task)
        try {
            await initializeLanguage();
            completeLanguage();
        } catch (e) {
            if (LOGGING.ENABLED && LOGGING.GENERAL) console.error("Failed to initialize language module.", e);
            completeLanguage(); // Still mark as complete to avoid blocking
        }

        // PRIORITY: Preload all critical static assets BEFORE menu initialization
        // This ensures flags, logos, and icon sprites are ready when needed (no delay)
        // - flags: needed when menu opens (language switcher)
        // - logos: needed for menu and UI
        // - icon sprites: needed for UI (icons.svg), menu (areas.svg), and cards (cards-icons.svg)
        try {
            const { preloadLanguageFlags, preloadLogos, preloadIconSprites } = await import('./utils/cache/static-assets-cache.js');
            // Preload all critical assets in parallel, but wait for them to complete
            await Promise.all([
                preloadLanguageFlags(),
                preloadLogos(),
                preloadIconSprites()
            ]);
        } catch (error) {
            // Don't block if preload fails, but log it
            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.warn('[App] Failed to preload static assets:', error);
            }
        }

        // Preload areas list early (for menu) - don't await, run in background
        import('./utils/cache/index.js').then(({ preloadAreasList }) => {
            preloadAreasList().catch(error => {
                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.warn('[App] Failed to preload areas list:', error);
                }
            });
        });

        // Wait for resources (images, fonts) to load (async, tracked internally)
        loading.waitForResources();

        // Initialize Map Module (tracked task)
        const mapModule = await import('./map/index.js');
        const map = mapModule.initializeMap();

        // Only load map data if configured to load on start (UNIFIED strategy)
        // For PER_AREA strategy, map data is loaded when an area is selected
        if (MAP_DATA_STRATEGY && MAP_DATA_STRATEGY.LOAD_ON_START) {
            await mapModule.loadGeoJSONMap(map);
        }

        // Wait for map tiles to finish loading
        loading.waitForMap(map);

        await mapModule.initializeMapButtons();
        initializeCards(map);

        // Mark map task as complete (map data loaded, tiles will be tracked separately)
        completeMap();

        // --- Route Resolution ---
        // Restore area from storage if none provided
        if (!areaSlug && AREA_SELECTOR.ENABLED) {
            const storedAreaSlug = getLastSelectedAreaSlug();
            if (storedAreaSlug) {
                const area = await getAreaBySlug(storedAreaSlug);
                if (area) {
                    if (LOGGING.ENABLED && LOGGING.GENERAL) console.log(`Restoring area from storage: ${storedAreaSlug}`);
                    areaSlug = storedAreaSlug;
                    updateUrl(areaSlug, null);

                    // Update map styles for UNIFIED strategy after restoring area
                    if (MAP_DATA_STRATEGY && MAP_DATA_STRATEGY.TYPE === 'UNIFIED') {
                        mapModule.updateMapStyles();
                    }
                }
            }
        }

        const route = resolveStartupRoute({ blogData, directIds, areaSlug, mode, params });

        switch (route.type) {
            case 'blog': {
                if (BLOG_ENABLED) {
                    await handleBlogPost(route.blogData);
                    loading.forceHide();
                    return;
                }
                // If blog is disabled but URL suggests blog, fall through to area-selector
                break;
            }
            case 'ids': {
                await loadSpotsByIds(route.ids, route.areaSlug);
                finalizeInitialization(null, null);
                return;
            }
            case 'area-selector': {
                if (AREA_SELECTOR.ENABLED) {
                    await initializeAreaSelector({
                        onAreaSelected: async (area) => {
                            if (area) {
                                await handleAreaSelection(area, null, [], mapModule);
                            }
                        }
                    });
                }
                finalizeInitialization(null, null);
                return;
            }
            case 'favorites':
            case 'explore':
            case 'search':
            case 'discover':
            case 'area-only': {
                const area = await getAreaBySlug(route.areaSlug);

                // Note: Preloading happens in handleAreaSelection() - no need to duplicate here
                if (!area) {
                    if (LOGGING.ENABLED && LOGGING.GENERAL) console.warn(`Area '${route.areaSlug}' not found. Showing selector.`);
                    if (AREA_SELECTOR.ENABLED) {
                        await initializeAreaSelector({
                            onAreaSelected: async (selectedArea) => {
                                if (selectedArea) {
                                    await handleAreaSelection(selectedArea, null, [], mapModule);
                                }
                            }
                        });
                    }
                    finalizeInitialization(null, null);
                    return;
                }

                // Map route to mode/params and reuse existing handler
                if (route.type === 'favorites') {
                    await handleAreaSelection(area, 'favorites', [route.idsString], mapModule);
                } else if (route.type === 'explore') {
                    await handleAreaSelection(area, 'explore', route.params, mapModule);
                } else if (route.type === 'search') {
                    await handleAreaSelection(area, 'search', route.params, mapModule);
                } else if (route.type === 'discover') {
                    await handleAreaSelection(area, 'discover', route.params, mapModule);
                } else {
                    await handleAreaSelection(area, null, [], mapModule);
                }
                return;
            }
            default: {
                finalizeInitialization(null, null);
                return;
            }
        }

    } catch (error) {
        if (LOGGING.ENABLED && LOGGING.GENERAL) console.error('Error initializing app:', error);
        alert('Error initializing application. Please check the console for details.');
        // Force hide loading on error
        const loading = document.getElementById('app-loading');
        if (loading) {
            const loadingManager = window.__loadingManager;
            if (loadingManager) {
                loadingManager.forceHide();
            } else {
                // Fallback to simple hide
                loading.classList.remove('visible');
                setTimeout(() => {
                    loading.style.display = 'none';
                }, 500);
            }
        }
    }
}

async function handleAreaSelection(area, mode, params, mapModule) {
    // Clear any spot selection from previous area - spots will be cleared when new area loads
    // This ensures dynamic action buttons return to default (share/location) state
    window.dispatchEvent(new CustomEvent('spotCleared'));

    // Preload area-specific data (packs, tags, maps) in background for instant navigation
    // Don't await - let it run in background while we continue with area selection
    import('./utils/cache/index.js').then(({ preloadAreaData }) => {
        preloadAreaData(area.id, area.slug).catch(error => {
            if (LOGGING.ENABLED && LOGGING.CACHE) {
                console.warn('[App] Failed to preload area data:', error);
            }
        });
    });

    // 1. Move Map to Area (Only if direct load; Selector handles its own transition)
    // If this is called from URL load, we want to setView immediately.
    // If called from Selector callback, map might already be moving.
    // We can safely setView here as a safeguard or for direct URLs.
    const map = mapModule.getMap();
    if (area.center_lat && area.center_lng) {
        // Check if we are already near the center? No, just set it.
        // For direct URL load, animate: false is good.
        // For Selector, it used transitionToArea which uses fade.
        // To avoid conflict, we can skip this if we know we came from selector?
        // Actually, map.setView is harmless if already at target.
        // But `transitionToArea` is async. 
        // Let's assume if we are here, we want to ensure we are at the spot.
        // If we came from URL (page reload), we are at default map center (world?), so we need to move.
        map.setView([area.center_lat, area.center_lng], area.zoom || 10);
    }

    // Update map styles for UNIFIED strategy to highlight selected area and dim others
    if (MAP_DATA_STRATEGY && MAP_DATA_STRATEGY.TYPE === 'UNIFIED') {
        mapModule.updateMapStyles();

        // Initialize area markers on map for unselected areas (UNIFIED mode only)
        // This allows users to click on unselected areas to switch to them
        if (MAP_DATA_STRATEGY.SELECT_AREA_FROM_MAP) {
            try {
                if (LOGGING.ENABLED && LOGGING.GENERAL) {
                    console.log('[App] Initializing area markers on map...');
                }

                // Fetch all areas to create markers for unselected ones
                const { getAreasList } = await import('./utils/spot-queries.js');
                const areas = await getAreasList();

                if (LOGGING.ENABLED && LOGGING.GENERAL) {
                    console.log('[App] Areas list fetched:', areas);
                }

                // Callback when area is selected from map marker
                const onAreaSelectedFromMap = async (selectedArea) => {
                    if (LOGGING.ENABLED && LOGGING.GENERAL) {
                        console.log('[App] Area selected from map marker:', selectedArea.slug);
                    }
                    // Recursively call handleAreaSelection to update everything for the new area
                    // Pass empty mode/params to show area-only (no specific flow)
                    await handleAreaSelection(selectedArea, null, [], mapModule);
                };

                // Initialize markers with callback
                await mapModule.initializeAreaMarkersOnMap(areas, area.slug, onAreaSelectedFromMap);

                if (LOGGING.ENABLED && LOGGING.GENERAL) {
                    console.log('[App] Area markers initialization complete');
                }
            } catch (error) {
                if (LOGGING.ENABLED && LOGGING.GENERAL) {
                    console.warn('[App] Failed to initialize area markers:', error);
                }
            }
        }
    }

    let activeFlow = null;
    let spotsLoaded = false;

    // --- Load minimal spots for area and display markers, then fetch full spot data for single source of truth ---
    // Only do this if not in explore/search/discover/favorites mode
    const isAreaOnly = !(
        (mode === 'explore' && URL_SETTINGS.explore) ||
        (mode === 'search' && URL_SETTINGS.search) ||
        ((mode === 'map' || mode === 'discover' || (mode === null && params.length > 0 && area)) && URL_SETTINGS.discover) ||
        ((mode === 'favorites' || mode === 'myfavorites') && URL_SETTINGS.favorites)
    );
    if (isAreaOnly) {
        const { SHOW_AREA_SPOTS } = await import('/settings.js');
        if (SHOW_AREA_SPOTS) {
            const { getMinimalSpotsForArea } = await import('./utils/spot-queries.js');
            // Fetch minimal spots to populate the cache, but DO NOT auto-display them.
            // The explore/search flows will use this cached minimal data when needed.
            try {
                const { minimal, ids } = await getMinimalSpotsForArea({ areaId: area.id, areaSlug: area.slug });
                if (minimal && minimal.length > 0) {
                    // Ensure minimal data is present in cache by mapping coordinates (client caches via fetchWithCache)
                    // Do not call `mapModule.displaySpots` or fetch full spots here to avoid showing all spots automatically.
                    if (LOGGING.ENABLED && LOGGING.CACHE) {
                        console.log(`[App] Cached minimal spots for area ${area.slug}: ${minimal.length} spots`);
                    }
                    spotsLoaded = true; // mark as loaded for flow purposes
                }
            } catch (err) {
                if (LOGGING.ENABLED && LOGGING.GENERAL) {
                    console.warn('[App] Failed to preload minimal spots for area (no display):', err);
                }
            }
        }
    }

    // 2. Handle Mode (Explore/Search/Discover)
    if (mode === 'explore' && URL_SETTINGS.explore) {
        // Handle explore mode: ?area/pack/tags (implicit explore) or ?area/explore/pack/tags (explicit)
        // Tags are separated by & in the URL (e.g., /tenerife/nature/beaches&charcos)
        const pack = params[0];
        let tags = [];
        let originalTags = [];
        if (params.length > 1 && params[1]) {
            // params[1] contains all tags joined with & (e.g., "beaches&charcos")
            // Split by & to get individual tags, limit to configured maximum
            const maxTags = EXPLORE_TAG_SETTINGS.MAX_TAGS_PER_EXPLORE;
            originalTags = params[1].split('&').filter(Boolean);
            tags = originalTags.slice(0, maxTags);
        }
        if (pack) {
            // If no tags provided, open Explore popup with pack pre-selected
            if (tags.length === 0) {
                await initializeExploreSearch();
                activeFlow = 'explore';
                spotsLoaded = false; // Don't load spots, just open the popup
            } else {
                // Tags provided - validate and load spots (or return empty if invalid)
                await loadSpotsByExplore({ area: area.slug, pack, tags }, mapModule);
                // Normalize URL if extra tags were present (keep only first 3)
                if (originalTags.length > tags.length) {
                    updateUrlForExplore(area.slug, pack, tags);
                }
                activeFlow = 'explore';
                spotsLoaded = true;
            }
        }
    }
    else if (mode === 'search' && URL_SETTINGS.search) {
        await loadSpotsBySearch({ city: area.slug, tags: params }, mapModule);
        activeFlow = 'search';
        spotsLoaded = true;
    }
    else if ((mode === 'map' || mode === 'discover' || (mode === null && params.length > 0 && area)) && URL_SETTINGS.discover) {
        // Handle map mode: ?area/mapSlug (implicit map) or ?area/map/mapSlug (explicit)
        // If mode is null but we have params and an area, assume it's a map slug
        const mapSlug = params[0];
        if (mapSlug) {
            await loadSpotsByDiscover(mapSlug, mapModule);
            activeFlow = 'discover';
            spotsLoaded = true;
        }
    }
    else if ((mode === 'favorites' || mode === 'myfavorites') && URL_SETTINGS.favorites) {
        const idsStr = params[0];
        if (idsStr) {
            const ids = idsStr.split(',').map(id => id.trim()).filter(Boolean);
            if (ids.length > 0) {
                await loadSpotsByIds(ids, area.slug);
                activeFlow = 'favorites';
                spotsLoaded = true;
                // Show special favorites card as the default banner in favorites mode
                try {
                    setCardsContext('special', { specialType: 'favorites' }, true);
                } catch (err) {
                    if (LOGGING.ENABLED && LOGGING.GENERAL) console.warn('Could not set favorites special cards context:', err);
                }
            }
        }
    }
    else if (activeFlow === 'area' || !activeFlow) {
        // Area-only (no specific flow): show area cards if available
        try {
            setCardsContext('area', { areaId: area.id, areaData: area }, true);
        } catch (err) {
            if (LOGGING.ENABLED && LOGGING.GENERAL) console.warn('Could not load area cards:', err);
        }
        activeFlow = 'area';
    }

    // 3. Finalize UI
    finalizeInitialization(activeFlow, spotsLoaded);

    // 4. Notify guide system that app is loaded (after UI is ready)
    try {
        const { notifyGuideAppLoaded } = await import('./utils/guide.js');
        await notifyGuideAppLoaded();
    } catch (err) {
        if (LOGGING.ENABLED && LOGGING.GENERAL) console.warn('Could not notify guide app loaded:', err);
    }
}

async function finalizeInitialization(activeFlow, spotsLoaded) {
    let autoOpen = false;
    if (!spotsLoaded) {
        if (HEADER_EXPLORE_DEFAULT) autoOpen = true;
        if (HEADER_DISCOVER_DEFAULT) autoOpen = true;
    }

    await initializeHeader({
        autoOpen: autoOpen,
        activeFlow: activeFlow
    });

    await initializeMenu();
    initializeAttribution();

    initializePerformanceDebug();

    // Loading will auto-hide when all tasks are complete
    // If we're here, we can mark UI initialization as complete
    const loading = window.__loadingManager;
    if (loading) {
        loading.completeTask('ui-initialization');
    } else {
        // Fallback: simple hide if manager not available
        const loadingEl = document.getElementById('app-loading');
        if (loadingEl) {
            loadingEl.classList.remove('visible');
            setTimeout(() => {
                loadingEl.style.display = 'none';
            }, 500);
        }
    }
}

// --- Logic Handlers ---

async function handleBlogPost(blogData) {
    if (!BLOG_ENABLED) return;

    try {
        // Lazy-load blog module only when needed
        const blogModule = await import('./blog/index.js');
        const blogPost = await blogModule.initializeBlog();
        if (blogPost) {
            requestAnimationFrame(async () => {
                await blogModule.createBlogPopup(blogPost);
            });
        }
    } catch (error) {
        if (LOGGING.ENABLED && LOGGING.BLOG) console.error("Failed to load blog post:", error);
    }
}

async function loadSpotsByIds(ids, areaSlug = null) {
    if (ids.length > URL_SETTINGS.MAX_SPOTS_TO_SHOW) {
        ids = ids.slice(0, URL_SETTINGS.MAX_SPOTS_TO_SHOW);
    }

    const FRONTEND_SPOT_LIMIT = 40;

    try {
        const spots = await getSpotsByIds(ids, { areaSlug });
        let spotsToDisplay = spots;

        if (Array.isArray(spots) && spots.length > FRONTEND_SPOT_LIMIT) {
            spotsToDisplay = spots.slice(0, FRONTEND_SPOT_LIMIT);
            const title = await getText('alerts.messages.spotsLimitTitle');
            let message = await getText('alerts.messages.spotsLimitMessage');
            message = message.replace('{limit}', FRONTEND_SPOT_LIMIT);
            showAlert({ title, message });
        }

        await updateMapView({ spotsToDisplay });
    } catch (err) {
        if (LOGGING.ENABLED && LOGGING.GENERAL) console.error('[App] Error fetching spots by IDs:', err);
    }
}

async function loadSpotsByExplore({ area, pack, tags }, mapModule) {
    try {
        const normalizedTags = tags.map(tag => tag === 'all' ? 'default' : tag);

        // Get area and pack IDs first (needed for cards)
        // Use cached fetch to prevent duplicate requests
        const { fetchWithCache } = await import('./utils/cache/index.js');
        const areas = await fetchWithCache(DATA_PATHS.EXPLORE_AREAS);
        const areaObj = areas.find(a => a.slug === area);

        if (!areaObj) {
            if (LOGGING.ENABLED && LOGGING.GENERAL) console.warn(`Area not found: ${area}`);
            return;
        }

        const packs = await fetchWithCache(`${DATA_PATHS.EXPLORE_PACKS}?areaId=${areaObj.id}`);
        const packObj = packs.find(p => p.slug === pack);

        if (!packObj) {
            if (LOGGING.ENABLED && LOGGING.GENERAL) console.warn(`Pack not found: ${pack}`);
            return;
        }

        // Get tag IDs if tags are provided (preserve the URL tag order)
        // Fetch tags once and pass to both spot loading and cards
        let tagIds = [];
        let tagList = null;
        if (normalizedTags.length > 0 && normalizedTags[0] !== 'default' && normalizedTags[0] !== 'all') {
            const { getTagsForAreaPack } = await import('./utils/spot-queries.js');
            tagList = await getTagsForAreaPack(areaObj.id, packObj.id);
            tagIds = normalizedTags
                .map(slug => tagList.find(t => t.slug === slug))
                .filter(Boolean)
                .map(t => t.id);
        }

        // DON'T clear caches - they're already differentiated by area in their keys
        // Caches are structured as: areaId-packId-tagIds, so different selections use different keys
        // This allows instant access to previously loaded data when switching between areas/tags
        const { triggerAreaBatch } = await import('./utils/cache/cards-cache.js');

        // PRIORITY ORDER: Trigger area-wide batch (start load sequence)
        triggerAreaBatch(areaObj.id);

        // Load spots (pass IDs to avoid redundant fetches)
        // This will trigger spots-ids.php (full content)
        const spots = await getSpotsByExplore({
            area,
            pack,
            tags: normalizedTags,
            areaId: areaObj.id,
            packId: packObj.id,
            tagIds: tagIds.length > 0 ? tagIds : undefined
        });

        // Always show cards immediately for explore (even if no spots match)
        // Set cards BEFORE map update so they appear before pan starts
        setCardsContext('explore', {
            areaId: areaObj.id,
            packId: packObj.id,
            tagIds: tagIds,
            options: {
                preFetchedTagList: tagList // Pass pre-fetched tag list to avoid duplicate fetch
            }
        }, true);

        if (spots.length > 0) {
            // Cards already set above, just update map
            await updateMapView({
                spotsToDisplay: spots
            });
        }
    } catch (err) {
        if (LOGGING.ENABLED && LOGGING.GENERAL) console.error('[App] Error fetching spots by Explore:', err);
    }
}

async function loadSpotsBySearch({ city, tags }, mapModule) {
    try {
        const spots = await getSpotsBySearch({ city, tags });
        if (spots.length > 0) {
            await updateMapView({ spotsToDisplay: spots, searchedTags: tags });
        }
    } catch (err) {
        if (LOGGING.ENABLED && LOGGING.GENERAL) console.error('[App] Error fetching spots by Search:', err);
    }
}

async function loadSpotsByDiscover(capsuleSlug, mapModule) {
    try {
        // Get map data first (needed for cards)
        const map = await getMapBySlug(capsuleSlug);

        // DON'T clear caches - they're already differentiated by area in their keys
        // Caches are structured by areaId/mapId, so different selections use different keys
        // This allows instant access to previously loaded data when switching between areas/maps
        const { preloadCardsForMap } = await import('./utils/cache/cards-cache.js');

        // Preload map cards FIRST (before spots) since they render first
        // This ensures map cards are ready immediately when context is set
        if (map && map.id) {
            preloadCardsForMap(map.id);
        }

        const spots = await getSpotsByDiscover(capsuleSlug);

        // Set cards BEFORE map update so they appear before pan starts
        if (map && map.id) {
            try {
                const lang = document.documentElement.lang || localStorage.getItem('language') || 'en';
                const mapData = {
                    name: map[`name_${lang}`] || map.name_en,
                    description: map[`description_${lang}`] || map.description_en
                };
                setCardsContext('map', {
                    mapId: map.id,
                    areaId: map.area, // Pass areaId for hierarchy fallback (resolves from area_id in DB)
                    mapData
                }, true);
            } catch (cardError) {
                if (LOGGING.ENABLED && LOGGING.GENERAL) {
                    console.warn('[App] Could not load cards for map:', cardError);
                }
            }
        }

        if (spots.length > 0) {
            // Cards already set above, just update map
            await updateMapView({
                spotsToDisplay: spots
            });
        }
    } catch (err) {
        if (LOGGING.ENABLED && LOGGING.GENERAL) console.error('[App] Error fetching spots by Discover capsule:', err);
    }
}

